import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRouteMatch, useHistory } from "react-router-dom";

import { fetchPublishedGatings } from "actions/gating";
import { fetchPage, searchPages } from "actions/pages";
import {
  createGatingConfig, updateGatingConfig, deleteGatingConfig
} from 'actions/storyboard';
import smartGatingIcon from "../../../../assets/images/smartGating.svg";

import GatingConfigurations from "components/gatings";
import InfiniteScroll from "components/InfiniteScroll";
import { getBaseUrl } from "services/util";
import storyboardRoleService from "services/storyboardRoles.service";

import _ from 'lodash';
import shortid from "shortid";
import cx from 'classnames';
import Truncate from "react-truncate";

import {
  Button,
  Image,
  Form,
  Dropdown
} from "react-bootstrap";

import styles from './websiteSettings.module.scss';


export const ContentGating = ({ storyboard }) => {
  const dispatch = useDispatch();

  const gatingConfigs = useSelector((state) => _.get(state, "storyboards.storyboard.item.gatingConfigurations", [])) || [];
  const [filteredGatings, setFilteredGatings] = useState([]);

  useEffect(() => {
    fetchPages();

    if (storyboard.page) {
      if (storyboard.page && storyboard.type === 'INTERACTIVE_CONTENT') {
        dispatch(fetchPage(storyboard.page.id));
      }
    }
  }, []);
  
  useEffect(() => {
    if (!gatingConfigs) return;

    let smartGating = null;
    const otherGatings = [];
  
    for (const config of gatingConfigs) {
      if (config?.smart && !smartGating) {
        smartGating = config;
      } else {
        otherGatings.push(config);
      }
    }
  
    const finalGatings = smartGating ? [smartGating, ...otherGatings] : otherGatings;
  
    setFilteredGatings(finalGatings);
  
    if (!smartGating) {
      const request = {
        uid: shortid(),
        name: 'Smart Gating',
        enabled: false,
        optional: false,
        smart: true,
        trigger: null,
        target: null,
      };
  
      dispatch(createGatingConfig(request));
    }
  }, [gatingConfigs, dispatch]);

  const createNewGating = () => {
    var request = {
      uid: shortid(),
      name: 'Untitled Gating',
      enabled: false,
      optional: false,
      trigger: { type: 'TIME', value: 10 },
      target: null
    }

    dispatch(createGatingConfig(request));
  }

  const fetchPages = () => {
    if (storyboard && storyboard.type !== 'INTERACTIVE_CONTENT') {
      var request = {
        storyboard: storyboard.id
      }

      dispatch(searchPages(request, 0, 50));
    }
  }

  const handleUpdate = (id, request) => {
    dispatch(updateGatingConfig(id, request));
  }

  const handleDelete = (id) => {
    dispatch(deleteGatingConfig(id)); 
  }

  const renderGating = (gatingConfig) => {
    // guard clause for SmartGating
    if (gatingConfig.smart) {
      if(storyboard.type !== 'SPATIAL') {
        return (
          <SmartGating
          key={gatingConfig.id}
          gatingConfig={gatingConfig}
          onUpdate={handleUpdate}
          />
        );
      } return null;
    }

    return (
      <>
        <GatingConfigurations key={gatingConfig.id}
          gatingConfig={gatingConfig}
          storyboard={storyboard}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          type={"page"}
          className={styles.gatingContent}
        />
        <hr className={styles.gatingSeperator} />
      </>
    )
  }

  return (
    <div id="contentGating" className={cx(styles.gatingConatiner)}>
      <div className={styles.gatingWrapper}>
        <div className={styles.headerSection}>
          <SiteSettingHeader
            title='Gating Triggers'
            description=''
          />
          <div className={styles.newGatingSection}>
            <Button disabled={!storyboardRoleService.canEditStoryboard(storyboard)} variant="secondary" onClick={createNewGating} className={styles.createGatingButton}><i className={cx('cleverstory-plus', styles.addIcon)} />New Gating</Button>
          </div>
        </div>
        {filteredGatings.map(renderGating)}
      </div>
    </div>
  )
}

const SiteSettingHeader = ({ title, description }) => {

  return (
    <header className={styles.siteSettingsHeader}>
      <h2 className={styles.title}>{title}</h2>
      <p className={styles.description}>{description}</p>
    </header>
  )
}

const SmartGating = ({
  gatingConfig,
  onUpdate,
}) => {

  let { url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);

  const gatingScreen = _.get(gatingConfig, 'gating');
  const uid = _.get(gatingConfig, 'uid');
  const enabled = _.get(gatingConfig, 'enabled');
  let trigger = _.get(gatingConfig, 'trigger');  
  
  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard.item", null));
  
  const gatings = useSelector(state => _.get(state, 'gatings.published.items', []));
  const loading = useSelector(state => _.get(state, 'gatings.published.loading', false));
  const pagination = useSelector(state => _.get(state, 'gatings.published.pagination', {}));
  const hasMoreItems = _.get(pagination, "currentPage") < _.get(pagination, "totalPages") - 1;

  const [selectedGating, setSelectedGating] = useState(null);
  const [toggleOn, setToggleOn] = useState(enabled);
  const [animated, setAnimated] = useState(false);

  const borderRef = useRef(null);

  const dispatch = useDispatch();

  useEffect(() => {
    handleFetchMappings(0);
  }, []);

  useEffect(() => {
    if(trigger && toggleOn) {
      onUpdate(uid, {
        ...trigger,
        smart: false,
      });
    }
  }, [trigger]);

  useEffect(() => {
    if (gatingScreen) {
      setSelectedGating(gatingScreen.name);
    } else {
      onUpdate(uid, {
        ...trigger,
        enabled: false,
      });
      setToggleOn(false);
    }
  }, [gatingScreen, gatings]);

  useEffect(() => {
    const border = borderRef.current;
    if (!border) return;
  
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if(animated) {
            setAnimated(false);
            return;
          }
          observer.unobserve(entry.target); // only once
          setAnimated(true);  
        }
      });
    });

    observer.observe(border);

    return () => {
      if (border) observer.unobserve(border);
    };
  }, []);

  const handleFetchMappings = (page) => {
    dispatch(fetchPublishedGatings({
      storyboard: storyboard.id,
      published: true
    }, page, 50));
  }

  const loadMore = () => {
    const nextPage = pagination.currentPage + 1;
    if (nextPage >= pagination.totalPages) {
      return;
    }
    handleFetchMappings(nextPage);
  }

  const handleGatingScreen = (data) => {
    var selected = gatings.find(item => item.id === data);
    if (selected) {
      onUpdate(uid, { gating: selected });
    }
  }

  const renderScreenOption = (selectedData) => {
    return (
      <span className={styles.optionName}>
        <Truncate lines={2} width={150}>
          {selectedData}
        </Truncate>
      </span>
    )
  }

  const handleEditGating = (e) => {
    e.stopPropagation();

    if (!gatingScreen) {
      return;
    }

    const win = window.open(`${baseUrl}/gating/${gatingScreen.id}`, "_blank");
    win.focus();
  }

  const toggleHandler = () => {
    if(!selectedGating) return;

    setToggleOn(!toggleOn);

    let request = {
      ...trigger,
      smart: false
    };
    if(!toggleOn) {
      request = {
        trigger : null,
        smart: true,
        optional: true,
        enabled: true,
      };
    } else {
      request = {
        smart: true,
        trigger: null,
        optional: false,
        enabled: false
      };
    }
    onUpdate(uid, request);
  };

  return (
    <div className={styles.smartGatingWrapper}>
      <div className={cx(styles.smartGatingContentBorder, {[styles.smartGatingContentBorderAnimation]: animated })} ref={borderRef} />
      <div className={styles.smartGatingSection}>
        <div className={styles.smartGatingHeaderSection}>
          <div className={styles.smartGatingHeader}>
            <div className={styles.smartGatingHeaderContent}>
              <div className={styles.smartGatingIconSection}>
                <Image
                  className={styles.pulseImage}
                  src={smartGatingIcon}
                  width={22}
                  height={22}
                />
                <Image
                  className={styles.pulseImage2}
                  src={smartGatingIcon}
                  width={22}
                  height={22}
                />
              </div>
              <p className={styles.smartGatingTitle}>Smart Gating</p>
                {toggleOn ? 
                  <p style={{ background: '#FFC600', color: '#424242' }} className={styles.smartGatingInActiveBtn}>
                    <span>ACTIVE</span>
                  </p> :
                  <p style={{ background: '#E0E0E0' }} className={styles.smartGatingInActiveBtn}>
                    <span>INACTIVE</span>
                  </p> 
                }
            </div>
            <Form.Check
              type="switch"
              id="custom-switch"
              onChange={toggleHandler}
              checked={toggleOn}
              className={styles.toggleSwitch}
            />
          </div>
          <p className={styles.smartGatingDescription}>
            Smart gating dynamically triggers forms based on past reader engagements.
          </p>
        </div>
        <div className={styles.smartGatingContent}>
          <p>Gating Screen</p>
          <Dropdown className={styles.gatingDropdown}>
            <Dropdown.Toggle>
              {gatingScreen ? (
                <span className={styles.selectedName}>
                  <Truncate width={150}>{selectedGating}</Truncate>
                </span>
              ) : (
                <span className={styles.selectedName}>Select Gating</span>
              )}
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <InfiniteScroll
                className={cx(styles.scrollable)}
                loading={loading}
                hasMore={hasMoreItems}
                loadMore={loadMore}
              >
                {gatings.map((data, index) => {
                  const activeGatingScreen =
                    gatingScreen && gatingScreen.id === data.id;
                  return (
                    <Dropdown.Item
                      active={activeGatingScreen}
                      onClick={() => handleGatingScreen(data.id)}
                      key={index}
                    >
                      {renderScreenOption(data.name, activeGatingScreen)}
                    </Dropdown.Item>
                  );
                })}
              </InfiniteScroll>
            </Dropdown.Menu>
          </Dropdown>
          <Button
            className={styles.smartGatingEditButton}
            variant="info"
            onClick={handleEditGating}
          >
            <i className={cx("cleverstory-edit", styles.editIcon)} />
          </Button>
        </div>
      </div>
    </div>
  );
};
